'use client';

import React from 'react';
import { MovingItemsFlowAdapter } from '@/features/visit-counsel/moving-items-flow';
import type { MovingItemRequest } from '@/entities/visit-counsel/model/room-observing.types';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { RoomTypeValue } from '@/entities/visit-counsel/model/house-sketch.types';
import type { EditTarget } from './EditTargetSelector';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface EditObservingFlowProps {
  /** 상담 ID */
  visitCounselId: string;
  /** room 또는 additional space 세션 ID */
  sessionId: string;
  /** additional space 여부 */
  isAdditionalSpaceMoving: boolean;
  /** UI 라벨 ("거실", "1번 방" 등) */
  roomLabel: string;
  /** 기록 중인 방 타입 — spec.roomTypes 기준 ItemPicker 섹션 분류 */
  roomType?: RoomTypeValue;
  /** 기존 저장된 짐 (prefill) */
  initialMovingItems?: MovingItemRequest[];
  /** 편집 대상 ('items' | 'video' | 'both') */
  editTarget: EditTarget;
  /**
   * 짐 기록 가능 여부 — `MovingItemsFlowAdapter.canRecordMovingItems` 로 그대로 전달.
   * `false` 면 짐 단계가 강제로 스킵되고 영상만 진행된다 (사다리차/계단 등 운반 경로).
   * undefined 또는 true 면 기존 동작 (방 / 짐 기록 가능 추가공간).
   */
  canRecordMovingItems?: boolean;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  /** 편집 완료 후 호출 */
  onAfterDone: (counsel: VisitCounselView) => void;
  /** 뒤로가기 (picker 화면으로 복귀) */
  onBack: () => void;
  /** BottomSheet portal target */
  container?: HTMLElement | null;
}

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

/**
 * 완료된 방의 편집 플로우 오케스트레이터.
 *
 * EditTarget 에 따라 MovingItemsFlowAdapter 를 적절히 설정해 호출한다.
 *   - 'items': 짐 기록 피커만 표시, 영상 스텝 스킵
 *   - 'video': 영상 촬영 스텝만 표시, 짐 피커 스킵
 *   - 'both' : 짐 → 영상 순서로 진행 (기존 편집 플로우와 동일)
 *
 * 완료된 방 편집이므로 isFirstStart=false 고정 (start API 아닌 record API 사용).
 */
export function EditObservingFlow({
  visitCounselId,
  sessionId,
  isAdditionalSpaceMoving,
  roomLabel,
  roomType,
  initialMovingItems,
  editTarget,
  canRecordMovingItems,
  showToast,
  refetch,
  onAfterDone,
  onBack,
  container,
}: EditObservingFlowProps) {
  return (
    <MovingItemsFlowAdapter
      key={`${sessionId}-${editTarget}`}
      visitCounselId={visitCounselId}
      sessionId={sessionId}
      isAdditionalSpaceMoving={isAdditionalSpaceMoving}
      isFirstStart={false}
      roomLabel={roomLabel}
      roomType={roomType}
      initialMovingItems={initialMovingItems}
      editTarget={editTarget}
      canRecordMovingItems={canRecordMovingItems}
      showToast={showToast}
      refetch={refetch}
      onAfterDone={onAfterDone}
      onCancel={onBack}
      container={container}
    />
  );
}
